import React, { useState, useEffect, useCallback } from 'react'
import { Dialog, IconButton, Flex, Text } from '@radix-ui/themes'
import {
  Cross2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  ZoomInIcon,
  ZoomOutIcon,
  TrashIcon,
} from '@radix-ui/react-icons'

import { makeImage } from '../utils/makeImage'
import { cn } from '../lib/utils'

import HeicSafeImage from './HeicSafeImage'

interface ImageLightboxProps {
  isOpen: boolean
  onClose: () => void
  imageUrls: string[]
  initialIndex: number
  onDownload?: (imageUrl: string) => void
  onDelete?: (imageUrl: string, index: number) => void
  canDelete?: boolean
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  onClose,
  imageUrls,
  initialIndex,
  onDownload,
  onDelete,
  canDelete = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const minZoom = 1
  const maxZoom = 3
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Update current index when initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  // Reset states when lightbox opens or image changes
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setHasError(false)
      setIsZoomed(false)
      setZoomLevel(1)
      setPanPosition({ x: 0, y: 0 })
      setIsDragging(false)
    }
  }, [isOpen, currentIndex])

  // Navigation functions
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1))
  }, [imageUrls.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % imageUrls.length)
  }, [imageUrls.length])

  // Zoom functionality
  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => {
      const newZoom = Math.min(maxZoom, prev + 0.5)
      setIsZoomed(newZoom > minZoom)

      return newZoom
    })
    setPanPosition({ x: 0, y: 0 })
  }, [maxZoom, minZoom])

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const newZoom = Math.max(minZoom, prev - 0.5)
      setIsZoomed(newZoom > minZoom)

      if (newZoom === minZoom) {
        setPanPosition({ x: 0, y: 0 })
      }

      return newZoom
    })
  }, [minZoom])

  const resetZoom = useCallback(() => {
    setZoomLevel(minZoom)
    setIsZoomed(false)
    setPanPosition({ x: 0, y: 0 })
  }, [minZoom])

  // Handle image click for zoom (optional - can be disabled if preferred)
  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      // Only zoom in on first click, then let user use buttons
      e.stopPropagation()

      if (!isZoomed) {
        zoomIn()
      }
    },
    [isZoomed, zoomIn]
  )

  // Pan functionality
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isZoomed) {
        setIsDragging(true)
        setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y })
        e.preventDefault()
      }
    },
    [isZoomed, panPosition]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && isZoomed) {
        const newX = e.clientX - dragStart.x
        const newY = e.clientY - dragStart.y

        // Calculate boundaries based on image and container sizes
        const maxX = Math.max(0, (imageSize.width * zoomLevel - containerSize.width) / 2)
        const maxY = Math.max(0, (imageSize.height * zoomLevel - containerSize.height) / 2)

        setPanPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        })
      }
    },
    [isDragging, isZoomed, dragStart, imageSize, containerSize, zoomLevel]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Update container size on resize
  useEffect(() => {
    const updateContainerSize = () => {
      setContainerSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    updateContainerSize()
    window.addEventListener('resize', updateContainerSize)

    return () => window.removeEventListener('resize', updateContainerSize)
  }, [])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowLeft':
          if (!isZoomed) {
            e.preventDefault()
            goToPrevious()
          }
          break
        case 'ArrowRight':
          if (!isZoomed) {
            e.preventDefault()
            goToNext()
          }
          break
        case 'Escape':
          e.preventDefault()

          if (isZoomed) {
            // First ESC resets zoom completely
            resetZoom()
          } else {
            // Second ESC closes lightbox
            onClose()
          }
          break
        case ' ':
          e.preventDefault()

          if (!isZoomed) {
            zoomIn()
          }
          break
        case 'Enter':
          e.preventDefault()
          zoomIn()
          break
        case '+':
        case '=':
          e.preventDefault()
          zoomIn()
          break
        case '-':
        case '_':
          e.preventDefault()
          zoomOut()
          break
      }
    },
    [isOpen, isZoomed, goToPrevious, goToNext, onClose, zoomIn, zoomOut, resetZoom]
  )

  // Attach keyboard event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const currentImageUrl = imageUrls[currentIndex]
  const fullImageUrl = makeImage(currentImageUrl)

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false)
    setHasError(false)

    // Update image size for pan calculations
    const img = e.target as HTMLImageElement
    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight,
    })
  }

  const handleImageError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload(currentImageUrl)
    } else {
      // Default download behavior
      const link = document.createElement('a')
      link.href = fullImageUrl
      link.download = `imagen-${currentIndex + 1}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  // Delete functionality
  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (onDelete && imageUrls.length > 0) {
      const currentImageUrl = imageUrls[currentIndex]
      onDelete(currentImageUrl, currentIndex)
      setShowDeleteConfirm(false)

      // If this was the last image, close the lightbox
      if (imageUrls.length === 1) {
        onClose()
      } else {
        // Adjust current index if we deleted the last image
        if (currentIndex >= imageUrls.length - 1) {
          setCurrentIndex(Math.max(0, imageUrls.length - 2))
        }
      }
    }
  }, [onDelete, imageUrls, currentIndex, onClose])

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  if (!isOpen) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content
        className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm"
        style={{
          maxWidth: '100vw',
          maxHeight: '100vh',
          width: '100vw',
          height: '100vh',
          margin: 0,
          padding: 0,
          borderRadius: 0,
          border: 'none',
        }}
      >
        {/* Header with controls */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
          <Flex justify="between" align="center">
            <div className="flex items-center gap-4">
              <Text size="4" className="text-white font-medium">
                {currentIndex + 1} / {imageUrls.length}
              </Text>

              {imageUrls.length > 1 && !isZoomed && (
                <div className="flex items-center gap-2">
                  <IconButton
                    variant="ghost"
                    size="3"
                    className="text-white hover:bg-white/20"
                    onClick={goToPrevious}
                    aria-label="Imagen anterior"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </IconButton>

                  <IconButton
                    variant="ghost"
                    size="3"
                    className="text-white hover:bg-white/20"
                    onClick={goToNext}
                    aria-label="Imagen siguiente"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </IconButton>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-black/30 rounded-lg p-1">
                <IconButton
                  variant="ghost"
                  size="2"
                  className="text-white hover:bg-white/20"
                  onClick={zoomOut}
                  disabled={zoomLevel <= minZoom}
                  aria-label="Alejar"
                >
                  <ZoomOutIcon className="w-4 h-4" />
                </IconButton>

                <Text size="2" className="text-white px-2 min-w-[3rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </Text>

                <IconButton
                  variant="ghost"
                  size="2"
                  className="text-white hover:bg-white/20"
                  onClick={zoomIn}
                  disabled={zoomLevel >= maxZoom}
                  aria-label="Acercar"
                >
                  <ZoomInIcon className="w-4 h-4" />
                </IconButton>
              </div>

              <IconButton
                variant="ghost"
                size="3"
                className="text-white hover:bg-white/20"
                onClick={handleDownload}
                aria-label="Descargar imagen"
              >
                <DownloadIcon className="w-5 h-5" />
              </IconButton>

              {/* Delete Button */}
              {canDelete && (
                <IconButton
                  variant="ghost"
                  size="3"
                  className="text-white hover:bg-red-500/50"
                  onClick={handleDeleteClick}
                  aria-label="Eliminar imagen"
                >
                  <TrashIcon className="w-5 h-5" />
                </IconButton>
              )}

              <IconButton
                variant="ghost"
                size="3"
                className="text-white hover:bg-white/20"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <Cross2Icon className="w-5 h-5" />
              </IconButton>
            </div>
          </Flex>
        </div>

        {/* Main image area */}
        <div
          className="absolute inset-0 flex items-center justify-center p-16 overflow-hidden"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
          )}

          {hasError ? (
            <div className="text-center text-white">
              <div className="text-6xl mb-4">⚠️</div>
              <Text size="4">Error al cargar la imagen</Text>
              <Text size="2" className="text-white/70 mt-2">
                No se pudo cargar la imagen solicitada
              </Text>
            </div>
          ) : (
            <HeicSafeImage
              src={fullImageUrl}
              alt={`Imagen ${currentIndex + 1} de ${imageUrls.length}`}
              className={cn(
                'object-contain transition-all duration-300 select-none',
                isLoading ? 'opacity-0' : 'opacity-100',
                isZoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in'
              )}
              style={{
                maxWidth: isZoomed ? 'none' : '100%',
                maxHeight: isZoomed ? 'none' : '100%',
                transform: `scale(${zoomLevel}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                transformOrigin: 'center center',
                userSelect: 'none',
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              draggable={false}
            />
          )}
        </div>

        {/* Navigation buttons (for desktop) - hidden when zoomed */}
        {imageUrls.length > 1 && !isZoomed && (
          <>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors duration-200"
              onClick={goToPrevious}
              aria-label="Imagen anterior"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>

            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors duration-200"
              onClick={goToNext}
              aria-label="Imagen siguiente"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Bottom indicators */}
        {imageUrls.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
            <div className="flex gap-2 bg-black/50 rounded-full px-3 py-2">
              {imageUrls.map((_, index) => (
                <button
                  key={index}
                  className={cn(
                    'w-2 h-2 rounded-full transition-all duration-200',
                    index === currentIndex ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                  )}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Ir a imagen ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Touch areas for mobile navigation - only when not zoomed */}
        {imageUrls.length > 1 && !isZoomed && (
          <>
            <div
              className="absolute left-0 top-0 w-1/4 h-full z-10 md:hidden"
              onClick={goToPrevious}
              aria-label="Imagen anterior"
            />
            <div
              className="absolute right-0 top-0 w-1/4 h-full z-10 md:hidden"
              onClick={goToNext}
              aria-label="Imagen siguiente"
            />
          </>
        )}

        {/* Help text */}
        {currentIndex === initialIndex && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 text-white/70 text-sm text-center bg-black/50 rounded-lg px-3 py-2 animate-pulse">
            {isZoomed
              ? 'Arrastra para navegar | Usa botones +/- para zoom | ESC Reset'
              : 'Clic para acercar | +/- Zoom | ← → Navegar | ESC Cerrar'}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/75">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
              <div className="mb-4">
                <TrashIcon className="w-12 h-12 text-red-500 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar imagen</h3>
                <p className="text-gray-600">
                  ¿Estás seguro de que quieres eliminar esta imagen? Esta acción no se puede deshacer.
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <button
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  onClick={handleDeleteCancel}
                >
                  Cancelar
                </button>
                <button
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  onClick={handleDeleteConfirm}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default ImageLightbox
